import type { PointerEvent } from 'react'
import { motion, Transition } from 'framer-motion'
import { twMerge } from 'tailwind-merge'
import { miniDragControlsRef, useIsMiniDragging, useSetMiniDragHandleHovered } from '../../../atoms'
import { useIsMiniAnchoredLeft } from '../side-context'

interface MiniDragHandleProps {
  className?: string
}

const miniTransition: Transition = {
  type: 'spring',
  bounce: 0,
  duration: 0.4,
}

export function MiniDragHandle({ className }: MiniDragHandleProps) {
  const isDragging = useIsMiniDragging()
  const isOnLeftSide = useIsMiniAnchoredLeft()
  const setDragHandleHovered = useSetMiniDragHandleHovered()

  const handlePointerDown = (e: PointerEvent) => {
    // Capture pointer to ensure we get all subsequent events
    ;(e.target as HTMLElement).setPointerCapture?.(e.pointerId)
    miniDragControlsRef.current?.start(e)
  }

  return (
    <motion.div
      layout
      transition={miniTransition}
      className={twMerge(
        'group relative z-10 flex cursor-grab touch-none items-center justify-center',
        isOnLeftSide ? 'pl-1.5 pr-1' : 'pl-1 pr-1.5',
        'after:absolute after:-bottom-3 after:-top-3 after:content-[""]',
        isOnLeftSide
          ? 'after:-left-1.5 after:-right-0.5 after:rounded-l-full'
          : 'after:-left-0.5 after:-right-1.5 after:rounded-r-full',
        isDragging && 'cursor-grabbing',
        className,
      )}
      onPointerDown={handlePointerDown}
      onClick={(e) => e.stopPropagation()}
      onMouseEnter={() => setDragHandleHovered(true)}
      onMouseLeave={() => setDragHandleHovered(false)}
    >
      <DragHandleIcon className="text-foreground opacity-70 transition-all group-hover:scale-[1.03] group-hover:opacity-100 group-active:scale-[0.97]" />
    </motion.div>
  )
}

function DragHandleIcon({ className }: { className?: string }) {
  return (
    <svg width="8" height="15" viewBox="0 0 8 15" fill="none" xmlns="http://www.w3.org/2000/svg" className={className}>
      <path
        d="M1.53906 2.29175C1.95328 2.29175 2.28906 1.95596 2.28906 1.54175C2.28906 1.12753 1.95328 0.791748 1.53906 0.791748C1.12485 0.791748 0.789062 1.12753 0.789062 1.54175C0.789062 1.95596 1.12485 2.29175 1.53906 2.29175Z"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="1.58333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.03906 2.29175C6.45328 2.29175 6.78906 1.95596 6.78906 1.54175C6.78906 1.12753 6.45328 0.791748 6.03906 0.791748C5.62485 0.791748 5.28906 1.12753 5.28906 1.54175C5.28906 1.95596 5.62485 2.29175 6.03906 2.29175Z"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="1.58333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1.53906 7.79175C1.95328 7.79175 2.28906 7.45596 2.28906 7.04175C2.28906 6.62753 1.95328 6.29175 1.53906 6.29175C1.12485 6.29175 0.789062 6.62753 0.789062 7.04175C0.789062 7.45596 1.12485 7.79175 1.53906 7.79175Z"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="1.58333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.03906 7.79175C6.45328 7.79175 6.78906 7.45596 6.78906 7.04175C6.78906 6.62753 6.45328 6.29175 6.03906 6.29175C5.62485 6.29175 5.28906 6.62753 5.28906 7.04175C5.28906 7.45596 5.62485 7.79175 6.03906 7.79175Z"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="1.58333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M1.53906 13.2917C1.95328 13.2917 2.28906 12.956 2.28906 12.5417C2.28906 12.1275 1.95328 11.7917 1.53906 11.7917C1.12485 11.7917 0.789062 12.1275 0.789062 12.5417C0.789062 12.956 1.12485 13.2917 1.53906 13.2917Z"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="1.58333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M6.03906 13.2917C6.45328 13.2917 6.78906 12.956 6.78906 12.5417C6.78906 12.1275 6.45328 11.7917 6.03906 11.7917C5.62485 11.7917 5.28906 12.1275 5.28906 12.5417C5.28906 12.956 5.62485 13.2917 6.03906 13.2917Z"
        stroke="currentColor"
        strokeOpacity="0.2"
        strokeWidth="1.58333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}
